import { type RpcRangeOrder } from '@chainflip/rpc/parsers';
import { type BaseChainflipAsset } from '@chainflip/utils/chainflip';
import { tickToRate } from '@chainflip/utils/tickMath';
import { useMutation } from '@tanstack/react-query';
import BigNumber from 'bignumber.js';
import { ReadonlyButton } from '@/shared/components/dashboards/Readonly';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useToast from '@/shared/hooks/useToast';
import useTracking from '@/shared/hooks/useTracking';
import { chainflipAssetMap, TokenAmount } from '@/shared/utils';
import { liquidityToTokenAmounts } from '@/shared/utils/tickMath';
import usePoolPrice from '../../hooks/usePoolPrice';
import { useStateChainAccount } from '../../hooks/useStateChainAccount';
import { LiquidityProvisionEvents, type LiquidityProvisionTrackEvents } from '../../types/track';

export const CloseRangeOrderButton = ({
  order,
  baseAsset,
}: {
  order: RpcRangeOrder;
  baseAsset: BaseChainflipAsset;
}) => {
  const { sendExtrinsic } = useStateChainAccount();
  const lowPrice = tickToRate(order.range.start, baseAsset);
  const highPrice = tickToRate(order.range.end, baseAsset);
  const toast = useToast('close-range-order-button');
  const track = useTracking<LiquidityProvisionTrackEvents>();
  const { selectedAccount: polkadotAccount } = usePolkadot();

  const { rangeOrderPrice = BigNumber(0) } = usePoolPrice(baseAsset);
  const tokenAmounts = liquidityToTokenAmounts({
    liquidity: order.liquidity.toString(),
    baseAsset,
    currentRate: rangeOrderPrice,
    lowerTick: order.range.start,
    upperTick: order.range.end,
  });
  const earnedFeesBaseAsset = TokenAmount.fromAsset(order.fees_earned.base.toString(), baseAsset);
  const earnedFeesQuoteAsset = TokenAmount.fromAsset(order.fees_earned.quote.toString(), 'Usdc');

  const { mutate: close, isPending } = useMutation({
    mutationFn: async () => {
      toast.loading('Submitting transaction to the State Chain...');
      track(LiquidityProvisionEvents.CloseRangeOrder, {
        props: {
          path: window.location.pathname,
          scAccount: polkadotAccount?.idSs58 ?? '-',
          position: 'open_orders',
          type: 'limit',
          lowPrice: lowPrice.toFixed(),
          highPrice: highPrice.toFixed(),
          amountBaseAsset: tokenAmounts.baseAsset.toFixed() ?? '-',
          amountQuoteAsset: tokenAmounts.quoteAsset.toFixed() ?? '-',
          collectableFeeBaseAsset: earnedFeesBaseAsset.toFixed(),
          collectableFeeQuoteAsset: earnedFeesQuoteAsset.toFixed(),
        },
      });

      return sendExtrinsic('set-range-order', {
        baseAsset,
        quoteAsset: 'Usdc',
        orderId: BigInt(order.id),
        lowerTick: order.range.start,
        upperTick: order.range.end,
        maxBaseAmount: TokenAmount.fromAsset(0, baseAsset),
        maxQuoteAmount: TokenAmount.fromAsset(0, 'Usdc'),
      });
    },
    onSuccess() {
      track(LiquidityProvisionEvents.CloseRangeOrderSuccess, {
        props: {
          path: window.location.pathname,
          scAccount: polkadotAccount?.idSs58 ?? '-',
          position: 'open_orders',
          type: 'limit',
          lowPrice: lowPrice.toFixed(),
          highPrice: highPrice.toFixed(),
          amountBaseAsset: tokenAmounts.baseAsset.toFixed() ?? '-',
          amountQuoteAsset: tokenAmounts.quoteAsset.toFixed() ?? '-',
          collectableFeeBaseAsset: earnedFeesBaseAsset.toFixed(),
          collectableFeeQuoteAsset: earnedFeesQuoteAsset.toFixed(),
        },
      });

      toast.success(
        `Range order from ${lowPrice.toFixed(2)} ${
          chainflipAssetMap.Usdc.symbol
        } to ${highPrice.toFixed(2)} ${chainflipAssetMap.Usdc.symbol} for ${
          chainflipAssetMap[baseAsset].symbol
        } closed`,
      );
    },
    onError(err) {
      toast.error('Error while submitting transaction', { description: err.message });
    },
  });

  return (
    <ReadonlyButton
      type="secondary-standard"
      size="small"
      loading={isPending && 'iconOnly'}
      onClick={() => close()}
    >
      Close
    </ReadonlyButton>
  );
};
